<template>
  <article class="pi-area" :class="{ 'is-focused': focused }" tabindex="-1" :aria-labelledby="ids.title">
    <header class="pi-area__head">
      <h3 :id="ids.title" class="pi-area__title">{{ card.label }}</h3>
      <pi-definition-tooltip :entry="card.entry" :label="card.label"/>
    </header>

    <div v-if="primary" class="pi-area__primary">
      <div class="pi-area__line">
        <router-link v-if="primary.route" :to="primary.route" class="pi-link pi-area__name">{{ primary.label }}</router-link>
        <span v-else class="pi-area__name">{{ primary.label }}</span>
        <pi-definition-tooltip :entry="primary.entry" :label="primary.label"/>
        <template v-if="primary.badge">
          <span class="pi-badge" :class="'pi-badge--' + primary.badge.tone" :title="primary.badge.title || null">{{ primary.badge.label }}</span>
          <pi-definition-tooltip :info-key="primary.badge.infoKey" :label="primary.badge.label"/>
        </template>
      </div>
      <p class="pi-area__value">{{ primary.valueText }}</p>
      <p v-if="primary.reachText" class="pi-area__line pi-area__reach">
        <span>{{ primary.reachText }}</span>
        <pi-definition-tooltip v-if="primary.hasFeature" info-key="areas.share" label="Participação"/>
        <pi-definition-tooltip v-else info-key="areas.companies" label="Empresas"/>
      </p>
      <p class="pi-area__line pi-area__delta">
        <span><span class="pi-area__key">Δ 7 dias:</span> {{ primary.delta7 || noComparison }}</span>
        <pi-definition-tooltip info-key="areas.delta7" label="Δ 7 dias"/>
      </p>
      <p v-if="primary.rateText" class="pi-area__line pi-area__delta">
        <span><span class="pi-area__key">Taxa:</span> {{ primary.rateText }}<template v-if="primary.rateSub"> ({{ primary.rateSub }})</template></span>
        <pi-definition-tooltip info-key="areas.rate" label="Taxa"/>
      </p>
    </div>

    <div v-if="card.others.length" class="pi-area__list">
      <table :id="ids.list" class="pi-table pi-area__table">
        <caption class="pi-sr">Outras métricas de {{ card.label }}</caption>
        <thead>
        <tr>
          <pi-th v-for="column in columns" :key="column.key" :column="column"/>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in rows" :key="row.key">
          <td>
            <router-link v-if="row.route" :to="row.route" class="pi-link pi-area__metric">{{ row.label }}</router-link>
            <span v-else>{{ row.label }}</span>
            <pi-definition-tooltip :entry="row.entry" :label="row.label"/>
            <span v-if="row.badge || row.sensitive" class="pi-area__tags">
              <template v-if="row.badge">
                <span class="pi-badge" :class="'pi-badge--' + row.badge.tone" :title="row.badge.title || null">{{ row.badge.label }}</span>
                <pi-definition-tooltip :info-key="row.badge.infoKey" :label="row.badge.label"/>
              </template>
              <template v-if="row.sensitive">
                <span class="pi-badge pi-badge--muted">Sensível</span>
                <pi-definition-tooltip info-key="areas.sensitive" label="Métrica sensível"/>
              </template>
            </span>
          </td>
          <td class="is-num">{{ row.valueText }}</td>
          <td class="is-num">{{ row.companiesText }}</td>
          <td class="is-num">{{ row.delta7 || empty }}</td>
        </tr>
        </tbody>
      </table>
      <button v-if="card.others.length > shortCount" type="button" class="pi-area__toggle" :aria-expanded="expanded ? 'true' : 'false'"
              :aria-controls="ids.list" @click="$emit('toggle')">{{ toggleLabel }}</button>
    </div>

    <footer class="pi-area__foot">
      <span v-if="card.coverage.text" class="pi-area__coverage" :class="{ 'is-partial': card.coverage.partial }">{{ card.coverage.text }}</span>
      <router-link :to="card.askRoute" class="pi-link pi-area__ask">Pergunte ao especialista</router-link>
    </footer>
  </article>
</template>

<script>
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import piTh from "./piTh.vue";
import { screenColumns } from "./piColumns.js";
import { EMPTY } from "./piFormat.js";
import { AREA_METRIC_COLUMNS, AREA_SHORT_LIST, shortList, toggleText } from "./piAreasModel.js";

let sequence = 0;

export default {
  name: "pi-area-card",
  components: { piDefinitionTooltip, piTh },
  props: {
    card: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
    focused: { type: Boolean, default: false },
  },
  data() {
    sequence += 1;
    return {
      ids: { title: "pi-area-" + sequence + "-title", list: "pi-area-" + sequence + "-list" },
      columns: screenColumns(AREA_METRIC_COLUMNS),
      shortCount: AREA_SHORT_LIST,
      empty: EMPTY,
      noComparison: "sem retrato para comparar",
    };
  },
  computed: {
    primary() {
      return this.card.primary;
    },
    rows() {
      return shortList(this.card.others, this.expanded);
    },
    toggleLabel() {
      return toggleText(this.card.others.length, this.expanded);
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-area {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #e3e8f0;
  border-radius: 6px;
  background: #ffffff;
}

.pi-area.is-focused {
  border-color: #1b59f8;
  box-shadow: 0 0 0 2px #dbe6fd;
}

.pi-area__head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.pi-area__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1f2933;
}

.pi-area__primary {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef0f2;
}

.pi-area__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.pi-area__name {
  font-size: 13px;
  font-weight: 600;
}

.pi-area__value {
  margin: 4px 0 2px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2933;
}

.pi-area__reach,
.pi-area__delta {
  margin: 2px 0 0;
  font-size: 12px;
  color: #4e5560;
}

.pi-area__key {
  font-weight: 600;
}

.pi-area__list {
  min-width: 0;
  overflow-x: auto;
}

.pi-area__table {
  table-layout: auto;
}

.pi-area__table th,
.pi-area__table td {
  padding: 6px 8px;
}

.pi-area__metric {
  font-size: 12px;
}

.pi-area__tags {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
}

.pi-area__toggle {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.pi-area__toggle:hover,
.pi-area__toggle:focus {
  text-decoration: underline;
}

.pi-area__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  margin-top: auto;
  padding-top: 10px;
  font-size: 11px;
  color: #6b7280;
}

.pi-area__coverage.is-partial {
  color: #7a4f00;
  font-weight: 600;
}

.pi-area__ask {
  font-size: 12px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piAreaCard.vue